package com.flowyride.core.domain.analysis

import com.flowyride.core.domain.model.GeoPoint
import kotlin.math.PI
import kotlin.math.asin
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

private const val EARTH_RADIUS_METERS = 6_371_000.0

private fun Double.toRadians() = this * PI / 180.0
private fun Double.toDegrees() = this * 180.0 / PI

/** Great-circle distance, in meters. */
fun GeoPoint.distanceTo(other: GeoPoint): Double {
    val dLat = (other.latitude - latitude).toRadians()
    val dLon = (other.longitude - longitude).toRadians()
    val h = sin(dLat / 2).let { it * it } +
        cos(latitude.toRadians()) * cos(other.latitude.toRadians()) * sin(dLon / 2).let { it * it }
    return 2 * EARTH_RADIUS_METERS * asin(sqrt(h))
}

/** Initial bearing towards [other], in degrees within (-180, 180]. */
fun GeoPoint.bearingTo(other: GeoPoint): Double {
    val lat1 = latitude.toRadians()
    val lat2 = other.latitude.toRadians()
    val dLon = (other.longitude - longitude).toRadians()
    val y = sin(dLon) * cos(lat2)
    val x = cos(lat1) * sin(lat2) - sin(lat1) * cos(lat2) * cos(dLon)
    return atan2(y, x).toDegrees()
}

/** Point reached from here after [meters] on the initial [bearing] (degrees), longitude kept within ±180°. */
internal fun GeoPoint.destination(bearing: Double, meters: Double): GeoPoint {
    val angular = meters / EARTH_RADIUS_METERS
    val theta = bearing.toRadians()
    val lat1 = latitude.toRadians()
    val lon1 = longitude.toRadians()
    val lat2 = asin(sin(lat1) * cos(angular) + cos(lat1) * sin(angular) * cos(theta))
    val lon2 = lon1 + atan2(sin(theta) * sin(angular) * cos(lat1), cos(angular) - sin(lat1) * sin(lat2))
    val longitude = ((lon2.toDegrees() + 540.0) % 360.0) - 180.0
    return GeoPoint(lat2.toDegrees().coerceIn(-90.0, 90.0), longitude)
}

/** Signed smallest angle from [from] to [to], in degrees within [-180, 180). */
fun headingChange(from: Double, to: Double): Double = ((to - from + 540.0) % 360.0) - 180.0
